import React, { useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import reportService from '../../services/reportService';
import userService from '../../services/userService';
import projectService from '../../services/projectService';
import { formatHoursToHoursMinutes, formatDecimalHours, calculateTotalHours } from '../../utils/timeUtils';

const AdminDashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const [recentReports, setRecentReports] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [todayReports, setTodayReports] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Get current date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Get today's reports specifically
        const todayReportsData = await reportService.getAllReports({ date: today }, 1, 100);
        
        // Get recent reports for the table
        const recentReportsData = await reportService.getAllReports({}, 1, 5);
        setRecentReports(recentReportsData.reports);
        
        // Get all developers
        const developersData = await userService.getAllDevelopers();
        setDevelopers(developersData);
        
        // Get all projects
        const projectsData = await projectService.getAllProjects();
        setProjects(projectsData);
        
        // Set today's reports count
        setTodayReports(todayReportsData.total);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [today]);
  
  return (
    <div className="page-fade-in">
        {/* Stat Cards */}
        <Row className="mb-4">
          <Col md={3} className="mb-3">
            <Card className="h-100 hover-card" style={{ 
              border: 'none', borderRadius: 14, 
              background: 'linear-gradient(135deg, #fff 0%, #E8E0F0 100%)',
              borderLeft: '4px solid #7C6FA0'
            }}>
              <Card.Body className="text-center p-4">
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#7C6FA0', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Today's Reports</div>
                <div className="pastel-stat-number mb-2">{todayReports}</div>
                <p style={{ color: '#9E9E9E', fontSize: '0.82rem', marginBottom: 12 }}>Submitted today</p>
                <Button as={Link} to="/admin/reports" variant="outline-primary" size="sm" style={{ borderRadius: 50, padding: '0.3rem 1rem' }}>
                  View All
                </Button>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3} className="mb-3">
            <Card className="h-100 hover-card" style={{ 
              border: 'none', borderRadius: 14,
              background: 'linear-gradient(135deg, #fff 0%, #D4EDDA 100%)',
              borderLeft: '4px solid #6BAF7B'
            }}>
              <Card.Body className="text-center p-4">
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6BAF7B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Team Members</div>
                <div className="pastel-stat-number mb-2">{developers.length}</div>
                <p style={{ color: '#9E9E9E', fontSize: '0.82rem', marginBottom: 12 }}>Active users</p>
                <Button as={Link} to="/admin/register" variant="outline-success" size="sm" style={{ borderRadius: 50, padding: '0.3rem 1rem' }}>
                  Add User
                </Button>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3} className="mb-3">
            <Card className="h-100 hover-card" style={{ 
              border: 'none', borderRadius: 14,
              background: 'linear-gradient(135deg, #fff 0%, #FFF3CD 100%)',
              borderLeft: '4px solid #D4A843'
            }}>
              <Card.Body className="text-center p-4">
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#D4A843', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Projects</div>
                <div className="pastel-stat-number mb-2">{projects.length}</div>
                <p style={{ color: '#9E9E9E', fontSize: '0.82rem', marginBottom: 12 }}>Active projects</p>
                <Button as={Link} to="/admin/projects" variant="outline-warning" size="sm" style={{ borderRadius: 50, padding: '0.3rem 1rem' }}>
                  Manage
                </Button>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3} className="mb-3">
            <Card className="h-100 hover-card" style={{ 
              border: 'none', borderRadius: 14,
              background: 'linear-gradient(135deg, #fff 0%, #D6E6F5 100%)',
              borderLeft: '4px solid #6BA3C7'
            }}>
              <Card.Body className="text-center p-4">
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6BA3C7', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Quick Export</div>
                <div style={{ fontSize: '2.5rem', lineHeight: 1, marginBottom: 8 }}>&#128196;</div>
                <p style={{ color: '#9E9E9E', fontSize: '0.82rem', marginBottom: 12 }}>Download reports</p>
                <Button 
                  variant="outline-info"
                  size="sm"
                  style={{ borderRadius: 50, padding: '0.3rem 1rem' }}
                  onClick={() => reportService.exportReports({ startDate: today, endDate: today })}
                >
                  Export CSV
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Recent Reports Table */}
        <Row>
          <Col>
            <Card style={{ border: '1px solid #EEEEEE', borderRadius: 14 }}>
              <Card.Header style={{ 
                background: 'linear-gradient(135deg, #E8E0F0, #D6E6F5)', 
                borderBottom: 'none', borderRadius: '14px 14px 0 0',
                padding: '1rem 1.25rem'
              }}>
                <h5 style={{ fontWeight: 700, color: '#7C6FA0', marginBottom: 0, fontSize: '1rem' }}>Recent Reports</h5>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <Table striped hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Developer</th>
                        <th>Hours</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentReports.length > 0 ? (
                        recentReports.map(report => (
                          <tr key={report.id}>
                            <td>{new Date(report.date).toLocaleDateString()}</td>
                            <td>{report.user_name}</td>
                            <td>
                              {formatHoursToHoursMinutes(report.task_details && report.task_details.length > 0 ? calculateTotalHours(report.task_details) : report.hours_worked)}
                              <div style={{ color: '#9E9E9E', fontSize: '0.78rem' }}>({formatDecimalHours(report.task_details && report.task_details.length > 0 ? calculateTotalHours(report.task_details) : report.hours_worked)} hours)</div>
                              {(!report.task_details || report.task_details.length === 0) && (
                                <div style={{ color: '#C97B7B', fontSize: '0.7rem' }}>Task details missing</div>
                              )}
                            </td>
                            <td>
                              <Button
                                as={Link}
                                to={`/admin/reports/${report.id}`}
                                variant="outline-primary"
                                size="sm"
                                style={{ borderRadius: 8, fontSize: '0.82rem' }}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center" style={{ color: '#9E9E9E', padding: '2rem' }}>
                            No reports found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
                <div className="text-center mt-3">
                  <Button as={Link} to="/admin/reports" variant="primary" size="sm" style={{ borderRadius: 50, padding: '0.4rem 1.5rem' }}>
                    View All Reports
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
    </div>
  );
};

export default AdminDashboard;
